import { ProductionCardStatus } from './production-card';
import { UnitOfMeasureType } from '../item/enum/UnitOfMeasureType';

// Search command for production dashboard
export interface ProductionDashboardSearchCommand {
  workOrderNumber?: string;
  clientReference?: string;
  brandReference?: string;
  workOrderStatuses?: WorkOrderStatus[];
  productionCardNumber?: string;
  productionCardStatuses?: ProductionCardStatus[];
}

// Work order status enum
export enum WorkOrderStatus {
  Draft = 0,
  Submitted = 1,
  Approved = 2,
  InPlanning = 3,
  InProduction = 4,
  Packaged = 5,
  Shipped = 6,
  Voided = 7,
}

// Work order type enum
export enum WorkOrderType {
  Sample = 0,
  Bulk = 1,
  GreigeStockForDyeing = 2,
  ShortFall = 3,
  Linked = 4,
  BGrade = 5,
  GreigeOnly = 6,
}

// Work order term enum
export enum WorkOrderTerm {
  CommissionKnitting = 0,
  FullKnitting = 1,
  Dyeing = 2,
  FullPackage = 3,
  CommissionKnittingAndDyeing = 4,
}

// Unit of measure and quantity key DTO
export interface UomAndQuantityKeyDto {
  unitOfMeasure: UnitOfMeasureType;
  quantity: number;
}

// Finishing route lite information
export interface FinishingRouteLite {
  number: string;
  quantity: UomAndQuantityKeyDto[];
  rollsCount: number;
}

// Production card component summary (fabric level)
export interface ProductionCardComponentSummaryDto {
  fabricCode: string;
  plannedQuantity: UomAndQuantityKeyDto[];
  actualQuantity: UomAndQuantityKeyDto[];
  fabricDescription: string;
  finishingRouteLite: FinishingRouteLite;
  lastProcessName?: string;
  completedProcessesCount?: number;
  totalProcessesCount?: number;
  weighedQuantity: UomAndQuantityKeyDto[];
  weighedRollsCount: number;
}

// Production card summary DTO
export interface ProductionCardSummaryDto {
  productionCardId: string;
  productionCardNumber: string;
  status: ProductionCardStatus;
  colorReference: string;
  colorName: string;
  labDipNumber: string;
  plannedQuantity: UomAndQuantityKeyDto[];
  actualQuantity: UomAndQuantityKeyDto[];
  latestCycleStatus?: string;
  latestCycleQCStatus?: string;
  components: ProductionCardComponentSummaryDto[];
}

// Roll quantities DTO
export interface RollQuantitiesDto {
  unitOfMeasure: UnitOfMeasureType;
  knittedQuantity: UomAndQuantityKeyDto[];
  knittedRollsCount: number;
  batchedQuantity: UomAndQuantityKeyDto[];
  batchedRollsCount: number;
  dyedQuantity: UomAndQuantityKeyDto[];
  dyedRollsCount: number;
  weighedQuantity: UomAndQuantityKeyDto[];
  weighedRollsCount: number;
  finishedGoodsQuantity: UomAndQuantityKeyDto[];
  finishedGoodsRollsCount: number;
  pendingDispatchInstructionQuantity: UomAndQuantityKeyDto[];
  pendingDispatchInstructionRollsCount: number;
  dispatchedQuantity: UomAndQuantityKeyDto[];
  dispatchedRollsCount: number;
}

// Main work order DTO for dashboard
export interface ProductionDashboardWorkOrderDto {
  id: string;
  workOrderNumber: string;
  workOrderType: WorkOrderType;
  workOrderTerm: WorkOrderTerm;
  status: WorkOrderStatus;
  clientProductOrderNumbers: string[];
  clientCode: string;
  clientName: string;
  brandCode: string;
  brandName: string;
  requiredQuantity: UomAndQuantityKeyDto[];
  totalCurrentRollQuantities: RollQuantitiesDto;
  productionCards: ProductionCardSummaryDto[];
}

// Tree row model for production dashboard UI
export interface ProductionTreeRow {
  type: 'WORK_ORDER' | 'PRODUCTION_CARD' | 'FABRIC';
  // Work Order fields
  id?: string;
  workOrderNumber?: string;
  workOrderType?: WorkOrderType;
  workOrderTerm?: WorkOrderTerm;
  status?: WorkOrderStatus | ProductionCardStatus;
  clientProductOrderNumbers?: string[];
  clientCode?: string;
  clientName?: string;
  brandCode?: string;
  brandName?: string;
  requiredQuantityDisplay?: string;
  knittedQuantityDisplay?: string;
  batchedQuantityDisplay?: string;
  dyedQuantityDisplay?: string;
  weighedQuantityDisplay?: string;
  finishedGoodsQuantityDisplay?: string;
  pendingDispatchQuantityDisplay?: string;
  dispatchedQuantityDisplay?: string;
  rollsCount?: number;
  // Production Card fields
  productionCardId?: string;
  productionCardNumber?: string;
  colorReference?: string;
  colorName?: string;
  labDipNumber?: string;
  plannedQuantityDisplay?: string;
  actualQuantityDisplay?: string;
  latestCycleStatus?: string;
  latestCycleQCStatus?: string;
  // Fabric fields
  fabricCode?: string;
  fabricDescription?: string;
  finishingRouteNumber?: string;
  lastProcessName?: string;
  processProgress?: string;
  weighedRollsCount?: number;
  // Original data for modal
  originalWorkOrder?: ProductionDashboardWorkOrderDto;
}
